import {
  getCurrentScope,
  isRef,
  onScopeDispose,
  ref,
  toValue,
  watch,
  type MaybeRefOrGetter,
  type Ref,
} from 'vue';

/** Which Web Storage area a value lives in. */
export type NxStorageArea = 'local' | 'session';

export interface NxReadStoredOptions<T> {
  /**
   * Accepts or rejects the decoded value; a rejected value reads as the
   * fallback. Without it the decoded value must at least have the fallback's
   * type (`typeof`, arrays by `Array.isArray`); a `null`/`undefined` fallback
   * accepts anything.
   */
  validate?: (value: unknown) => value is T;
  /**
   * Turns the stored string into a value. Default: a string fallback reads the
   * raw string, everything else `JSON.parse` (and the raw string when it is not
   * JSON, so a legacy `setItem(key, 'grid')` still reads).
   */
  parse?: (raw: string) => unknown;
  storage?: NxStorageArea;
}

export interface NxWriteStoredOptions<T> {
  /** Default: strings are stored raw, everything else as JSON. */
  serialize?: (value: T) => string;
  storage?: NxStorageArea;
}

export type NxStoredRefOptions<T> = Omit<NxReadStoredOptions<T>, 'storage'> &
  Omit<NxWriteStoredOptions<T>, 'storage'> & { storage?: NxStorageArea };

/** Same-tab broadcast so two stored refs on one key stay in step. */
const SAME_TAB_EVENT = 'nx-stored-change';
interface SameTabDetail {
  area: NxStorageArea;
  key: string;
  raw: string | null;
}

/**
 * The storage area, or `null` where there is none (SSR) or touching it throws
 * (Safari private mode, blocked site data, sandboxed iframes).
 */
function storageArea(area: NxStorageArea = 'local'): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return area === 'session' ? window.sessionStorage : window.localStorage;
  } catch {
    return null;
  }
}

function getRaw(key: string, area?: NxStorageArea): string | null {
  try {
    return storageArea(area)?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function defaultParse(raw: string, fallback: unknown): unknown {
  if (typeof fallback === 'string') return raw;
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return raw;
  }
}

function defaultSerialize(value: unknown): string {
  return typeof value === 'string' ? value : JSON.stringify(value);
}

function sameKind(value: unknown, fallback: unknown): boolean {
  if (fallback === null || fallback === undefined) return true;
  if (Array.isArray(fallback)) return Array.isArray(value);
  if (typeof fallback === 'object') {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }
  if (typeof fallback === 'number') return typeof value === 'number' && Number.isFinite(value);
  return typeof value === typeof fallback;
}

function decode<T>(raw: string | null, fallback: T, options: NxReadStoredOptions<T>): T {
  if (raw === null) return fallback;
  let value: unknown;
  try {
    value = options.parse ? options.parse(raw) : defaultParse(raw, fallback);
  } catch {
    return fallback;
  }
  if (options.validate) return options.validate(value) ? value : fallback;
  return sameKind(value, fallback) ? (value as T) : fallback;
}

/**
 * Reads a stored value; any failure (no storage, blocked access, bad JSON, a
 * value of the wrong shape) returns `fallback`. The fallback itself is
 * returned as is — clone it before mutating.
 */
export function readStored<T>(key: string, fallback: T, options: NxReadStoredOptions<T> = {}): T {
  return decode(getRaw(key, options.storage), fallback, options);
}

function setRaw(key: string, raw: string | null, area?: NxStorageArea): boolean {
  const storage = storageArea(area);
  if (!storage) return false;
  try {
    if (raw === null) storage.removeItem(key);
    else storage.setItem(key, raw);
  } catch {
    return false; // quota exceeded or blocked — the value stays in memory only
  }
  try {
    window.dispatchEvent(
      new CustomEvent<SameTabDetail>(SAME_TAB_EVENT, {
        detail: { area: area ?? 'local', key, raw },
      })
    );
  } catch {
    /* no CustomEvent (very old runtimes) — cross-instance sync just skips */
  }
  return true;
}

/**
 * Stores a value; `undefined` removes the entry. Returns whether it was
 * written (false when storage is missing, blocked or full).
 */
export function writeStored<T>(
  key: string,
  value: T,
  options: NxWriteStoredOptions<T> = {}
): boolean {
  if (value === undefined) return setRaw(key, null, options.storage);
  let raw: string;
  try {
    raw = options.serialize ? options.serialize(value) : defaultSerialize(value);
  } catch {
    return false; // cyclic or otherwise unserialisable
  }
  return setRaw(key, raw, options.storage);
}

/** Removes a stored entry; returns whether storage was reachable. */
export function removeStored(key: string, options: { storage?: NxStorageArea } = {}): boolean {
  return setRaw(key, null, options.storage);
}

function cloneDefault<T>(value: T): T {
  if (typeof value !== 'object' || value === null) return value;
  try {
    return structuredClone(value);
  } catch {
    return value;
  }
}

/**
 * A ref kept in Web Storage: it starts with the stored value (or a copy of
 * `defaultValue`), writes every change back (deep, so in-place edits of
 * arrays and objects count) and follows changes made in other tabs and by
 * other stored refs on the same key.
 *
 * `key` may be a ref or getter — e.g. a per-user key — and the value is
 * re-read whenever it changes. An empty key keeps the value in memory only.
 * Nothing is written while the value still equals what was read, so an unset
 * entry keeps following a changed default. Listeners end with the calling
 * effect scope (component unmount).
 */
export function useNxStoredRef<T>(
  key: MaybeRefOrGetter<string | null | undefined>,
  defaultValue: T,
  options: NxStoredRefOptions<T> = {}
): Ref<T> {
  const area = options.storage ?? 'local';
  const readOptions: NxReadStoredOptions<T> = {
    validate: options.validate,
    parse: options.parse,
    storage: area,
  };
  const serialize = (value: T): string | null => {
    if (value === undefined) return null;
    try {
      return options.serialize ? options.serialize(value) : defaultSerialize(value);
    } catch {
      return null;
    }
  };

  let currentKey = toValue(key) || '';
  /** Serialised form of the value last read or written — writes skip it. */
  let settled: string | null = null;

  function load(): T {
    const raw = currentKey ? getRaw(currentKey, area) : null;
    const value =
      raw === null
        ? cloneDefault(defaultValue)
        : decode(raw, cloneDefault(defaultValue), readOptions);
    settled = serialize(value);
    return value;
  }

  const state = ref(load()) as Ref<T>;

  function adopt(raw: string | null): void {
    const value =
      raw === null
        ? cloneDefault(defaultValue)
        : decode(raw, cloneDefault(defaultValue), readOptions);
    settled = serialize(value);
    state.value = value;
  }

  const stops: Array<() => void> = [];

  stops.push(
    watch(
      state,
      (value) => {
        // The key changed in the same tick (user switch): the value may belong
        // to either key, so write neither — the key watcher loads the new one.
        if ((toValue(key) || '') !== currentKey) return;
        const raw = serialize(value);
        if (raw === settled) return;
        settled = raw;
        if (!currentKey) return;
        if (value === undefined) setRaw(currentKey, null, area);
        else if (raw !== null) setRaw(currentKey, raw, area);
      },
      { deep: true }
    )
  );

  if (isRef(key) || typeof key === 'function') {
    stops.push(
      watch(
        () => toValue(key) || '',
        (next) => {
          currentKey = next;
          const value = load();
          state.value = value;
        }
      )
    );
  }

  if (typeof window !== 'undefined') {
    const onStorage = (event: StorageEvent) => {
      if (!currentKey) return;
      // `key === null` is `storage.clear()` in another tab.
      if (event.key !== null && event.key !== currentKey) return;
      if (event.storageArea && event.storageArea !== storageArea(area)) return;
      adopt(event.key === null ? null : event.newValue);
    };
    const onSameTab = (event: Event) => {
      const detail = (event as CustomEvent<SameTabDetail>).detail;
      if (!detail || !currentKey || detail.area !== area || detail.key !== currentKey) return;
      if (detail.raw === settled) return;
      adopt(detail.raw);
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(SAME_TAB_EVENT, onSameTab);
    stops.push(() => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(SAME_TAB_EVENT, onSameTab);
    });
  }

  if (getCurrentScope()) onScopeDispose(() => stops.forEach((stop) => stop()));
  return state;
}
